import { AsyncFlateStreamHandler, Zip, ZipPassThrough } from 'fflate';

import * as XLSX from 'xlsx';

export type FileArchive = { [filePath: string]: Blob; };

export class FileHelper {

  static sanitiseFileName(input: string, replacement: string = '_'): string {

    // Replace illegal characters: / ? < > \ : * | " and control characters, and also remove the null byte.
    const illegalRegEx = /[\/\?<>\\:\*\|":\x00-\x1f\x80-\x9f]/g;
    let output = input.replace(illegalRegEx, replacement);

    // Remove leading/trailing periods and spaces (problematic on Windows/macOS).
    const boundaryRegEx = /^[\.\s]+|[\.\s]+$/g;
    output = output.replace(boundaryRegEx, '');

    // Add a prefix if it matches reserved Windows file names (CON, PRN, AUX, NUL, COM1, LPT1, etc.).
    const reservedRegEx = /^(con|prn|aux|nul|com[0-9]|lpt[0-9])(\..*)?$/i;
    output = reservedRegEx.test(output) ? `${replacement}${output}` : output;

    // Truncate if too long (most file systems cap at 255 bytes - we truncate to 250 to be even safer).
    output = output.substring(0, 250) || 'default_filename';

    return output;
  }

  static createJsonBlob(input: any, indentation: number = 2): Blob {
    const jsonString = JSON.stringify(input, null, indentation);
    const blob = new Blob([jsonString], { type: 'application/json' });
    return blob;
  }

  static createXlsxBlob<TRow extends object>(dataRows: TRow[], sheetName: string = 'Sheet1'): Blob {
    const workbook = XLSX.utils.book_new();
    const worksheet = XLSX.utils.json_to_sheet(dataRows, { cellDates: true, dateNF: 'yyyy/mm/dd hh:mm:ss.000' });
    XLSX.utils.book_append_sheet(workbook, worksheet, sheetName);
    const excelBuffer = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });
    const blob = new Blob([excelBuffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
    return blob;
  }

  static async generateZippedBlob(archive: FileArchive): Promise<Blob> {

    const chunks: Uint8Array[] = [];

    const streamHandlerCallBack: AsyncFlateStreamHandler = (err, chunk, isFinal) => {
      if (err) {
        throw err;
      }
      if (chunk.length > 0) {
        chunks.push(chunk);
      }
    };

    const zipStream = new Zip(streamHandlerCallBack);

    // Looping sequentially using a standard for...of loop to handle one file at a time, so as to avoid running out of memory.
    for (const [filePath, blob] of Object.entries(archive)) {
      const zipFileStream = new ZipPassThrough(filePath);
      zipStream.add(zipFileStream);

      const stream = blob.stream();
      const reader = stream.getReader();

      let done = false;
      while (!done) {
        const { value, done: streamDone } = await reader.read();
        done = streamDone;

        if (value && value.length > 0) {
          zipFileStream.push(value, streamDone);
        }
        else if (done) {
          zipFileStream.push(new Uint8Array(0), true);
        }
      }
      delete archive[filePath]; // making sure the Blob is released from memory as soon as possible
    }

    zipStream.end();

    return new Blob(chunks as BlobPart[], { type: 'application/zip' });
  }
}
